import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { StyledModal, colors } from '../theme/sharedComponents'

const LIGHTBOX_BG = '#000'

const Body = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${LIGHTBOX_BG};
  min-height: 60vh;

  @media (max-width: 575px) { min-height: 50vh; }
`

const Img = styled.img`
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  display: block;

  @media (max-width: 575px) { max-height: 60vh; }
`

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: ${colors.white};
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.15s;
  z-index: 1;

  &:hover { background: rgba(255, 255, 255, 0.3); }
  &.prev { left: 1rem; }
  &.next { right: 1rem; }
`

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
  background: ${LIGHTBOX_BG};
`

const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${({ $active }) => $active ? colors.white : 'rgba(255,255,255,0.3)'};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
`

interface ImageLightboxModalProps {
  show: boolean
  images: string[]
  initialIndex?: number
  alt?: string
  onHide: () => void
}

const ImageLightboxModal: React.FC<ImageLightboxModalProps> = ({ show, images, initialIndex = 0, alt = '', onHide }) => {
  const [index, setIndex] = useState(initialIndex)

  useEffect(() => {
    setIndex(initialIndex)
  }, [initialIndex, show])

  if (!images.length) return null

  return (
    <StyledModal show={show} onHide={onHide} size="xl" centered>
      <StyledModal.Header closeButton>
        <StyledModal.Title>
          {alt}{images.length > 1 ? ` — ${index + 1} / ${images.length}` : ''}
        </StyledModal.Title>
      </StyledModal.Header>

      <StyledModal.Body style={{ padding: 0 }}>
        <Body>
          {images.length > 1 && (
            <Arrow
              className="prev"
              onClick={() => setIndex(i => (i - 1 + images.length) % images.length)}
              aria-label="Previous image"
            >
              <i className="bi bi-chevron-left" />
            </Arrow>
          )}
          <Img src={images[index]} alt={`${alt} ${index + 1}`} />
          {images.length > 1 && (
            <Arrow
              className="next"
              onClick={() => setIndex(i => (i + 1) % images.length)}
              aria-label="Next image"
            >
              <i className="bi bi-chevron-right" />
            </Arrow>
          )}
        </Body>
        {images.length > 1 && (
          <Dots>
            {images.map((_, i) => (
              <Dot key={i} $active={i === index} onClick={() => setIndex(i)} aria-label={`Image ${i + 1}`} />
            ))}
          </Dots>
        )}
      </StyledModal.Body>
    </StyledModal>
  )
}

export default ImageLightboxModal
